/**
 * useAppUpdater.ts — renderer hook for the electron-updater IPC bridge.
 *
 * Exposes a clean state machine so any UI component can show update
 * notifications without talking to window.api directly.
 *
 * State transitions:
 *
 *   idle ──check()──► checking ──► up-to-date (back to idle)
 *                               └──► available ──download()──► downloading
 *                                                            └──► ready ──install()──► (app restarts)
 *
 * Usage:
 *   const { status, updateInfo, downloadProgress, check, download, install } = useAppUpdater();
 */
import { useState, useEffect, useCallback, useRef } from 'react';

export type UpdaterStatus =
  | 'idle'
  | 'checking'
  | 'up-to-date'
  | 'available'
  | 'downloading'
  | 'ready'
  | 'error';

export interface UpdateInfo {
  version: string;
  releaseDate: string;
  releaseNotes?: string;
}

export interface DownloadProgress {
  percent: number;
  bytesPerSecond: number;
  transferred: number;
  total: number;
}

export interface AppUpdaterState {
  /** Current updater status */
  status: UpdaterStatus;
  /** Available update info (set when status === 'available' | 'downloading' | 'ready') */
  updateInfo: UpdateInfo | null;
  /** Download progress (set when status === 'downloading') */
  downloadProgress: DownloadProgress | null;
  /** Last error message (set when status === 'error') */
  errorMessage: string | null;
  /** Trigger a check-for-updates request (idempotent while checking) */
  check: () => void;
  /** Begin downloading the available update (idempotent while downloading) */
  download: () => void;
  /** Quit and install the downloaded update */
  install: () => void;
}

/** Returns false when running outside Electron (browser dev / tests). */
function hasApi(): boolean {
  return typeof window !== 'undefined' && 'api' in window && 'updates' in (window as any).api;
}

export function useAppUpdater(): AppUpdaterState {
  const [status, setStatus] = useState<UpdaterStatus>('idle');
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Keep unsubscribe functions so we don't leak listeners on unmount.
  const unsubsRef = useRef<Array<() => void>>([]);

  useEffect(() => {
    if (!hasApi()) return;

    const api = (window as any).api.updates;

    const unsubs: Array<() => void> = [
      api.onUpdateAvailable((info: UpdateInfo) => {
        setUpdateInfo(info);
        setStatus('available');
      }),
      api.onDownloadProgress((p: DownloadProgress) => {
        setDownloadProgress(p);
        setStatus('downloading');
      }),
      api.onUpdateDownloaded((info: UpdateInfo) => {
        setUpdateInfo(info);
        setDownloadProgress(null);
        setStatus('ready');
      }),
      api.onError((err: { message: string }) => {
        setErrorMessage(err.message);
        setStatus('error');
      }),
    ];

    unsubsRef.current = unsubs;
    return () => { unsubs.forEach(fn => fn()); };
  }, []);

  const check = useCallback(() => {
    if (!hasApi()) return;
    if (status === 'checking') return;
    setStatus('checking');
    setErrorMessage(null);
    const api = (window as any).api.updates;
    api.check().then((result: { ok: boolean; version?: string | null; message?: string }) => {
      if (!result.ok) {
        setErrorMessage(result.message ?? 'Update check failed');
        setStatus('error');
      }
      // If result.ok but no update-available event fires, the onUpdateNotAvailable
      // event from main will eventually be received. If we never get it (network off),
      // we fall back to idle after a timeout.
      const timeout = setTimeout(() => {
        setStatus(prev => prev === 'checking' ? 'idle' : prev);
      }, 30_000);
      // Clean up on success events (onUpdateAvailable / onError will clear status)
      const cleanup = () => clearTimeout(timeout);
      unsubsRef.current.push(cleanup);
    }).catch((err: unknown) => {
      setErrorMessage((err as Error).message);
      setStatus('error');
    });
  }, [status]);

  const download = useCallback(() => {
    if (!hasApi()) return;
    if (status !== 'available') return;
    setStatus('downloading');
    const api = (window as any).api.updates;
    api.download().catch((err: unknown) => {
      setErrorMessage((err as Error).message);
      setStatus('error');
    });
  }, [status]);

  const install = useCallback(() => {
    if (!hasApi()) return;
    if (status !== 'ready') return;
    (window as any).api.updates.install();
  }, [status]);

  return { status, updateInfo, downloadProgress, errorMessage, check, download, install };
}
